import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const { FiMenu, FiX, FiPhone, FiMail, FiMapPin, FiClock, FiHeart, FiShield, FiSend, FiUser, FiFacebook } = FiIcons;

// Donation Modal Component
const DonationModal = ({ isOpen, onClose }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden relative"
          >
            <div className="bg-amber-700 p-6 flex justify-between items-center text-white">
              <div>
                <h3 className="text-2xl font-bold">Support Our Ministry</h3>
                <p className="text-amber-100 text-sm">Your gift makes a difference</p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                <SafeIcon icon={FiX} className="h-6 w-6" />
              </button>
            </div>

            <div className="p-8">
              <div className="mb-8 text-center">
                <div className="bg-amber-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-amber-100">
                  <SafeIcon icon={FiHeart} className="h-10 w-10 text-amber-600" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Giving with a Heart</h4>
                <p className="text-gray-600 leading-relaxed">
                  You can now choose any amount you'd like to contribute. Your generosity supports our community outreach and church maintenance.
                </p>
              </div>
              
              <div className="space-y-4">
                <form action="https://www.paypal.com/donate" method="post" target="_top" className="w-full">
                  <input type="hidden" name="hosted_button_id" value="TSEVS47SF2DKY" />
                  
                  <motion.button 
                    type="submit" 
                    name="submit"
                    whileHover={{ scale: 1.02 }} 
                    whileTap={{ scale: 0.98 }} 
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-5 px-6 rounded-2xl shadow-xl flex items-center justify-center gap-3 transition-colors text-lg"
                  >
                    <SafeIcon icon={FiShield} className="h-6 w-6" /> 
                    Proceed to Donation
                  </motion.button>
                  
                  <img alt="" border="0" src="https://www.paypal.com/en_US/i/scr/pixel.gif" width="1" height="1" className="mx-auto" />
                </form>

                <div className="flex flex-col items-center gap-3 mt-4">
                  <img 
                    src="https://www.paypalobjects.com/en_US/i/btn/btn_donateCC_LG.gif" 
                    alt="Donate with PayPal" 
                    className="h-10 opacity-80"
                  />
                  <p className="text-[10px] text-center text-gray-400 uppercase tracking-widest font-bold">
                    Securely processed by PayPal
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  
  const [contactData, setContactData] = useState({
    fullName: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const { fullName, email, phone } = contactData;
    const subject = encodeURIComponent(`New Contact Inquiry from ${fullName}`);
    const body = encodeURIComponent(
      `Full Name: ${fullName}\n` +
      `Email: ${email}\n` +
      `Phone: ${phone || 'Not provided'}\n\n` +
      `Sent from TFDCC Website`
    );
    window.location.href = `mailto:truthfaithdelechurch@gmail.com?subject=${subject}&body=${body}`;
  };

  const donateShake = {
    initial: { x: 100, opacity: 0 },
    animate: { 
      x: [100, 0, -5, 5, -5, 5, 0],
      opacity: 1 
    },
    transition: { 
      duration: 1.2,
      times: [0, 0.4, 0.5, 0.6, 0.7, 0.8, 1],
      ease: "easeOut",
      delay: 0.5
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <DonationModal isOpen={isDonationModalOpen} onClose={() => setIsDonationModalOpen(false)} />

      <motion.nav 
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrollY > 50 ? 'bg-white shadow-lg' : 'bg-white/90 backdrop-blur-sm'}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
      >
        <div className="max-w-7xl mx-auto px-4 h-16 flex justify-between items-center">
          <h1 className="text-xl font-bold text-amber-700 cursor-pointer" onClick={() => scrollToSection('home')}>TFDCC</h1>
          <div className="hidden md:flex items-center space-x-8">
            {['home', 'about', 'services', 'contact'].map(sec => (
              <button key={sec} onClick={() => scrollToSection(sec)} className="text-gray-700 hover:text-amber-700 text-sm font-medium capitalize">{sec}</button>
            ))}
            <motion.button 
              onClick={() => setIsDonationModalOpen(true)} 
              className="bg-amber-600 text-white px-6 py-2 rounded-full font-semibold shadow-md" 
              whileHover={{ scale: 1.05 }}
              {...donateShake}
            >
              DONATE
            </motion.button>
          </div>
          <button className="md:hidden text-gray-700" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="h-6 w-6" />
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            className="fixed inset-0 z-[60] bg-white flex flex-col p-8 md:hidden"
          >
            <div className="flex justify-between items-center mb-12">
              <span className="text-2xl font-bold text-amber-700">TFDCC</span>
              <button onClick={() => setIsMenuOpen(false)} className="p-2 bg-gray-100 rounded-full">
                <SafeIcon icon={FiX} className="h-8 w-8 text-gray-700" />
              </button>
            </div>
            <div className="flex flex-col gap-6">
              {['home', 'about', 'services', 'contact'].map(sec => (
                <button key={sec} onClick={() => scrollToSection(sec)} className="text-2xl font-bold text-gray-800 text-left capitalize border-b pb-4">{sec}</button>
              ))}
              <button onClick={() => { setIsDonationModalOpen(true); setIsMenuOpen(false); }} className="bg-amber-600 text-white py-4 rounded-2xl text-xl font-bold shadow-lg mt-4">DONATE NOW</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <section id="home" className="relative h-screen flex items-center justify-center text-center text-white p-4">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'linear-gradient(rgba(0,0,0,0.6),rgba(0,0,0,0.4)),url("https://images.unsplash.com/photo-1438232992991-995b7058bbb3?auto=format&fit=crop&w=2070&q=80")' }} />
        <div className="relative z-10 max-w-4xl">
          <motion.h1 className="text-5xl md:text-7xl font-bold mb-6" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            Truth Faith and Deliverance <span className="block text-amber-400">Community Church</span>
          </motion.h1>
          <motion.p className="text-xl mb-8 font-light" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            "We are a community based church, committed to serve the Lord Jesus by serving those whom we live among."
          </motion.p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => scrollToSection('services')} className="bg-amber-600 px-8 py-4 rounded-full font-bold shadow-xl">Join Us Sunday</button>
            <motion.button 
              onClick={() => setIsDonationModalOpen(true)} 
              className="bg-amber-600 px-8 py-4 rounded-full font-bold shadow-xl flex items-center gap-2"
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: [100, 0, -5, 5, -5, 5, 0], opacity: 1 }}
              transition={{ duration: 1.2, times: [0, 0.4, 0.5, 0.6, 0.7, 0.8, 1], ease: "easeOut", delay: 0.8 }}
            >
              <SafeIcon icon={FiHeart} /> Donate
            </motion.button>
          </div>
        </div>
      </section>

      <section id="about" className="py-24 bg-amber-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 tracking-tight">Our Heritage & Mission</h2>
            <div className="w-20 h-1.5 bg-amber-600 mx-auto rounded-full"></div>
          </div>
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="relative group">
              <div className="absolute -inset-4 bg-amber-200/50 rounded-[2rem] blur-2xl group-hover:bg-amber-300/50 transition-colors duration-500" />
              <img 
                src="https://images.unsplash.com/photo-1704151893532-b5c829ac1d1f?auto=format&fit=crop&w=1000&q=80" 
                className="relative rounded-[2rem] shadow-2xl w-full object-cover h-[500px]" 
                alt="Church Community" 
              />
            </div>
            <div className="space-y-8">
              <div className="space-y-6">
                <p className="text-xl text-gray-800 leading-relaxed font-medium">
                  Truth Faith and Deliverance Community Church stands as a beacon of hope in the heart of St. Petersburg, Florida. 
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Rooted in the rich traditions of the African American church experience, we celebrate our heritage while embracing all who seek to grow in faith and community.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Under the leadership of <span className="text-amber-800 font-bold">Pastor James Holt</span>, our church family is dedicated to serving not just our congregation, but our entire community. We believe that faith without works is dead.
                </p>
              </div>

              <div className="bg-white p-8 rounded-3xl border-l-8 border-amber-600 shadow-xl shadow-amber-900/5 italic text-amber-900 text-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                  <SafeIcon icon={FiHeart} className="w-12 h-12" />
                </div>
                "We are a community based church, committed to serve the Lord Jesus by serving those whom we live among."
              </div>
              
              <div className="pt-4 overflow-hidden">
                <motion.a 
                  href="https://www.facebook.com/TDFCC" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center justify-center gap-3 bg-[#1877F2] hover:bg-[#166fe5] text-white text-lg font-bold py-5 px-8 rounded-2xl shadow-xl shadow-blue-900/20 transition-all w-full sm:w-auto overflow-hidden"
                >
                  <motion.div
                    className="flex items-center gap-3"
                    initial={{ x: 150, opacity: 0 }}
                    whileInView={{ 
                      x: [150, 0, -6, 6, -6, 6, -6, 6, -6, 6, 0],
                      opacity: 1 
                    }}
                    viewport={{ once: true }}
                    transition={{ 
                      duration: 2,
                      times: [0, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 1],
                      ease: "easeOut",
                      delay: 0.3
                    }}
                  >
                    <SafeIcon icon={FiFacebook} className="h-6 w-6 shrink-0" />
                    <span className="whitespace-nowrap inline-block">
                      Join Us on Facebook
                    </span>
                  </motion.div>
                </motion.a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">Service Times</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { t: "Sunday School", v: "9:00 AM", d: "Sunday" },
              { t: "Morning Worship", v: "11:00 AM", d: "Sunday" },
              { t: "Prayer Service", v: "6:00 PM", d: "Wednesday" },
              { t: "Bible Study", v: "12:00 & 6:00 PM", d: "Wednesday" }
            ].map((s, i) => (
              <div key={i} className="bg-white p-8 rounded-3xl shadow-xl border border-amber-100 text-center hover:scale-105 transition-transform">
                <SafeIcon icon={FiClock} className="h-10 w-10 text-amber-600 mx-auto mb-4" />
                <h3 className="font-bold text-xl mb-2">{s.t}</h3>
                <p className="text-amber-700 font-bold">{s.v}</p>
                <p className="text-gray-500">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-24 bg-gradient-to-br from-gray-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get in Touch</h2>
            <div className="w-20 h-1.5 bg-amber-600 mx-auto rounded-full"></div>
            <p className="mt-6 text-gray-600">We'd love to hear from you. Reach out with any questions or prayer requests.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="bg-white p-8 rounded-3xl shadow-lg space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-amber-100 p-3 rounded-xl text-amber-600"><SafeIcon icon={FiMapPin} /></div>
                  <div><h4 className="font-bold">Address</h4><p className="text-gray-600">2525 30th Ave N, St. Petersburg, FL 33713</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-amber-100 p-3 rounded-xl text-amber-600"><SafeIcon icon={FiPhone} /></div>
                  <div><h4 className="font-bold">Phone</h4><p className="text-gray-600">(727) 821-7202</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-amber-100 p-3 rounded-xl text-amber-600"><SafeIcon icon={FiMail} /></div>
                  <div><h4 className="font-bold">Email</h4><p className="text-gray-600">truthfaithdelechurch@gmail.com</p></div>
                </div>
              </div>
              <div className="h-64 rounded-3xl overflow-hidden shadow-lg border-4 border-white">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3528.8!2d-82.7!3d27.8!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88c2e42a087a385f%3A0x6a1b6a1b6a1b6a1b!2s2525%2030th%20Ave%20N%2C%20St.%20Petersburg%2C%20FL%2033713!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus" width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"></iframe>
              </div>
            </div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-10 rounded-3xl shadow-xl border border-amber-100"
            >
              <h3 className="text-2xl font-bold mb-8 text-gray-900 flex items-center gap-3">
                <SafeIcon icon={FiSend} className="text-amber-600" />
                Contact Us
              </h3>
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                    <SafeIcon icon={FiUser} className="text-amber-600" /> Full Name
                  </label>
                  <input 
                    type="text" 
                    placeholder="Enter your full name"
                    required
                    value={contactData.fullName}
                    onChange={(e) => setContactData({...contactData, fullName: e.target.value})}
                    className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-xl focus:border-amber-500 focus:bg-white outline-none transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                    <SafeIcon icon={FiMail} className="text-amber-600" /> Email Address
                  </label>
                  <input 
                    type="email" 
                    placeholder="email@example.com"
                    required
                    value={contactData.email}
                    onChange={(e) => setContactData({...contactData, email: e.target.value})}
                    className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-xl focus:border-amber-500 focus:bg-white outline-none transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                    <SafeIcon icon={FiPhone} className="text-amber-600" /> Phone Number
                  </label>
                  <input 
                    type="tel" 
                    placeholder="(000) 000-0000"
                    value={contactData.phone}
                    onChange={(e) => setContactData({...contactData, phone: e.target.value})}
                    className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-xl focus:border-amber-500 focus:bg-white outline-none transition-all"
                  />
                </div>

                <motion.button 
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-4 rounded-xl shadow-lg transition-all flex items-center justify-center gap-3 mt-4"
                >
                  Submit Inquiry
                  <SafeIcon icon={FiSend} />
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-3 gap-12 border-b border-white/5 pb-12 mb-8">
          <div>
            <h3 className="text-2xl font-bold text-amber-400 mb-4">TFDCC Church</h3>
            <p className="text-gray-400">Serving the community with love and faith since our founding.</p>
          </div>
          <div>
            <h4 className="font-bold mb-4 uppercase text-gray-500">Quick Links</h4>
            <div className="grid grid-cols-2 gap-2 text-gray-300">
              <button onClick={() => scrollToSection('home')} className="text-left hover:text-amber-400">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-left hover:text-amber-400">About</button>
              <button onClick={() => scrollToSection('services')} className="text-left hover:text-amber-400">Services</button>
              <button onClick={() => scrollToSection('contact')} className="text-left hover:text-amber-400">Contact</button>
            </div>
          </div>
          <div>
            <h4 className="font-bold mb-4 uppercase text-gray-500">Contact</h4>
            <div className="text-gray-300 space-y-2">
              <p>(727) 821-7202</p>
              <p>truthfaithdelechurch@gmail.com</p>
            </div>
          </div>
        </div>
        <p className="text-center text-gray-500 text-sm">© 2026 Truth Faith and Deliverance Community Church. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default App;